import { useEffect, useRef, useCallback } from 'react';

/**
 * Custom hook to detect user idle time
 * @param {number} timeout - Idle timeout in milliseconds (default: 30 minutes)
 * @param {function} onIdle - Callback function when user becomes idle
 * @param {boolean} enabled - Whether the idle timer is enabled
 */
const useIdleTimer = (timeout = 30 * 60 * 1000, onIdle, enabled = true) => {
  const timeoutRef = useRef(null);
  const onIdleRef = useRef(onIdle);

  // Update the callback ref when it changes
  useEffect(() => {
    onIdleRef.current = onIdle;
  }, [onIdle]);

  // Reset the idle timer
  const resetTimer = useCallback(() => {
    if (!enabled) return;
    
    // Clear existing timeout
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    // Set new timeout
    timeoutRef.current = setTimeout(() => {
      if (onIdleRef.current) {
        onIdleRef.current();
      }
    }, timeout);
  }, [timeout, enabled]);

  useEffect(() => {
    if (!enabled) {
      // Clear timeout if disabled
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      return;
    }

    // Events that indicate user activity
    const activityEvents = [
      'mousedown',
      'mousemove',
      'keydown',
      'scroll',
      'touchstart',
      'click',
      'keypress',
    ];

    // Add event listeners for user activity
    activityEvents.forEach((event) => {
      document.addEventListener(event, resetTimer, { passive: true });
    });

    // Start the initial timer
    resetTimer();

    // Cleanup
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      activityEvents.forEach((event) => {
        document.removeEventListener(event, resetTimer);
      });
    };
  }, [resetTimer, enabled]);

  return { resetTimer };
};

export default useIdleTimer;
